import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";

export default function ShopSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | expired | error
  const [details, setDetails] = useState(null);

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    if (attempt > 10) {
      setStatus("pending");
      return;
    }
    try {
      const { data } = await api.get(`/shop/checkout/status/${sessionId}`);
      setDetails(data);
      if (data.payment_status === "paid") {
        setStatus("paid");
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => {
    poll();
  }, [poll]);

  return (
    <div data-testid="shop-success-page" className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
      <div className="max-w-2xl mx-auto border border-black/15 bg-white p-10">
        {status === "checking" && (
          <>
            <Loader2 size={36} className="animate-spin text-[#E6007A] mb-6" />
            <h1 className="font-display font-black text-4xl mb-4">Verifying payment…</h1>
            <p className="font-mono text-sm text-[#27272A]">Please don't close this page. This usually takes a few seconds.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="w-12 h-12 bg-[#FFD600] mb-6 flex items-center justify-center">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="font-display font-black text-5xl mb-4">Got it. Thanks.</h1>
            <p className="font-mono text-base text-[#27272A] leading-relaxed mb-6">
              Your order has been received and we'll start prepping it from the press.
              You'll get a confirmation email shortly with all the details.
            </p>
            {details && (
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#71717A] mb-8">
                Total paid: £{(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()} · Order ref: {sessionId?.slice(-12)}
              </p>
            )}
            <div className="flex gap-3 flex-wrap">
              <Link to="/shop" data-testid="success-back-shop" className="btn-ink bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.25em]">
                Back to shop
              </Link>
              <Link to="/" className="bg-white text-black border border-black px-6 py-3 font-mono text-xs uppercase tracking-[0.25em]">
                Home
              </Link>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <div className="w-12 h-12 bg-[#00E5FF] mb-6 flex items-center justify-center">
              <Loader2 size={24} />
            </div>
            <h1 className="font-display font-black text-4xl mb-4">Still processing…</h1>
            <p className="font-mono text-sm text-[#27272A] mb-6">
              Your payment is taking a moment to confirm. We'll send the email as soon as it clears — feel free to close this page.
            </p>
            <Link to="/shop" className="btn-ink bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.25em]">Back to shop</Link>
          </>
        )}

        {(status === "error" || status === "expired") && (
          <>
            <div className="w-12 h-12 bg-[#E6007A] text-white mb-6 flex items-center justify-center">
              <XCircle size={24} />
            </div>
            <h1 className="font-display font-black text-4xl mb-4">Something went wrong.</h1>
            <p className="font-mono text-sm text-[#27272A] mb-6">
              {status === "expired" ? "The checkout session expired before payment completed." : "We couldn't confirm your payment. No charge has been taken."}
            </p>
            <Link to="/shop" className="btn-ink bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.25em]">Try again</Link>
          </>
        )}
      </div>
    </div>
  );
}
